import { SEO } from "@/components/SEO";
import { lazy, Suspense } from "react";
import { Link } from "wouter";
import HomeContentServer from "@/pages/home-content";
import { organizationSchema } from "@/lib/organization";

function loadHomeContentWhenIdle() {
  return new Promise<typeof import("@/pages/home-content")>((resolve) => {
    const load = () => {
      void import("@/pages/home-content").then(resolve);
    };

    const scheduleLoad = () => {
      const browserWindow = window as Window & {
        requestIdleCallback?: (callback: () => void, options: { timeout: number }) => number;
      };

      if (browserWindow.requestIdleCallback) {
        browserWindow.requestIdleCallback(load, { timeout: 3000 });
      } else {
        globalThis.setTimeout(load, 1000);
      }
    };

    if (document.readyState === "complete") {
      scheduleLoad();
    } else {
      window.addEventListener("load", scheduleLoad, { once: true });
    }
  });
}

const HomeContent = import.meta.env.SSR
  ? HomeContentServer
  : lazy(loadHomeContentWhenIdle);

export default function Home() {
  return (
    <>
      <SEO
        title="TLC Associates LLC | Landscape Maintenance Contractor | Norfolk County, Massachusetts"
        description="Landscape maintenance services across Norfolk County, Massachusetts, from TLC Associates LLC for residential and commercial properties."
        path="/"
        schema={organizationSchema}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        href={`${import.meta.env.BASE_URL}hero/homepage-mobile-480.avif`}
        media="(max-width: 600px)"
        fetchPriority="high"
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        href={`${import.meta.env.BASE_URL}hero/homepage-mobile-800.avif`}
        media="(min-width: 601px) and (max-width: 768px)"
        fetchPriority="high"
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        href={`${import.meta.env.BASE_URL}hero/homepage-desktop.avif`}
        media="(min-width: 769px)"
        fetchPriority="high"
      />
      <section className="relative w-full min-h-[80vh] flex flex-col items-center justify-center">
        <div className="absolute inset-0 z-0">
          <picture>
            <source
              type="image/avif"
              media="(max-width: 600px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-mobile-480.avif`}
            />
            <source
              type="image/avif"
              media="(min-width: 601px) and (max-width: 768px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-mobile-800.avif`}
            />
            <source
              type="image/avif"
              media="(min-width: 769px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-desktop.avif`}
            />
            <source
              type="image/webp"
              media="(max-width: 600px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-mobile-480.webp`}
            />
            <source
              type="image/webp"
              media="(min-width: 601px) and (max-width: 768px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-mobile-800.webp`}
            />
            <source
              type="image/webp"
              media="(min-width: 769px)"
              srcSet={`${import.meta.env.BASE_URL}hero/homepage-desktop.webp`}
            />
            <img
              src={`${import.meta.env.BASE_URL}hero/homepage-mobile-480.webp`}
              alt="Lakefront property with flowering garden beds maintained by TLC Associates"
              width={480}
              height={853}
              fetchPriority="high"
              className="w-full h-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-[#1a3a1a]/45"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-24 md:mt-7 pb-8 md:pb-4">
          <h1 className="pt-[30px] text-white/90 text-[15px] md:text-[18px] lg:text-[20px] font-semibold uppercase tracking-[0.15em] mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Landscape Maintenance Contractor Serving Norfolk County, Massachusetts
          </h1>
          <p className="text-white text-4xl md:text-[52px] lg:text-[64px] font-bold mb-[29px] md:mb-8 leading-[1.1]" style={{ fontFamily: "'Fraunces', serif" }}>
            Stop Gambling on Landscapers.<br />You Grow It, We Know It.
          </p>
          <p className="text-white/90 text-xl md:text-2xl mb-9 md:mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
            TLC Associates LLC provides landscape maintenance and property services for homeowners and commercial properties across Norfolk County.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link 
              href="/contact#estimate-form" 
              className="w-full sm:w-auto bg-[#c84060] hover:bg-[#b53553] text-white px-8 py-4 rounded-lg font-medium text-lg transition-all transform hover:scale-[1.02] shadow-lg"
            >
              Request Your Free Estimate
            </Link>
            <a 
              href="tel:5085051539" 
              className="w-full sm:w-auto text-white text-lg font-medium px-8 py-4 hover:text-[#f5c828] transition-colors"
            >
              Or call 508-505-1539
            </a>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <HomeContent />
      </Suspense>
    </>
  );
}
